/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { BaseService } from './base.service';
import { ApiResp, Operator } from '@app/types/global.type';
import {
  EInvoice,
  EInvoiceInfo,
  EInvoiceListPayload,
  FilterKey,
  InvoiceCount,
  SchedulerLogsList,
} from '@app/types/taxpayer.type';
import { downloadBlob } from '@app/utils/misc';
import dayjs from 'dayjs';

export interface CreateEInvoice {
  invType: string;
  docType: string;
  currencyCode: string;
  tranactionType: string;
  currencyExchangeRate: string;
}
interface SubmitEInvoice {
  ids: string[];
}

interface cancelEinvoice {
  reason: string;
  uuids: string[];
}

interface EInvoiceList {
  data: EInvoice[];
  totalRecords: number;
}

interface SchedulerLogs {
  submissionStatsInvoiceResponses: SchedulerLogsList[];
  pageNumber: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
}

export interface SchedulerLogsParams {
  page?: number;
  size?: number;

  tin?: string;
  status?: string;
  submissionDateFrom?: string;
  submissionDateTo?: string;
  userTimeZone?: string;

  // search keys
  submissionId?: string;
  uploadId?: string;
  salesCount?: string;
  purchaseCount?: string;
}

export interface PepolFOVCriteria {
  field: string;
  operator: string;
  value: string | boolean | number | Date | string[];
}

export interface SharePepolPayload {
  fieldCriteriaList: PepolFOVCriteria[];
  sortCriteria: {
    field: string;
    direction: string;
  };
}

export interface EinvoiceEmail {
  message: string;
  subject: string;
  to: string;
  cc: string;
  filePath: string;
  url: string;
  supplierTin: string;
  buyerTin: string;
  counterParty: string;
  fieldCriteriaList: {
    field: FilterKey;
    operator: Operator;
    value: string | string[] | number | number[] | Date | Date[];
  }[];
}

export interface VerifyEinvoice {
  id: string;
  uuid: string;
  submissionUid: string;
  longId: string;
  status: string;
  publicURL: string;
  salesInv?: boolean;
  issuedInv: boolean;
}

interface Field {
  name: string;
  included: boolean;
  editable: boolean;
  sequence: number;
}

export interface CustomDownload {
  group: string;
  sequence: number;
  fields: Field[];
  selected: boolean;
  totalCount: number;
  selectedCount: number;
}

export interface VendorAndLabels {
  id: number;
  label: string;
  uniqueId: string;
}
export interface FieldCriteriaList {
  field: string;
  operator: string;
  value: string | boolean | number | string[];
}
export interface SortCriteria {
  field: string;
  direction: 'DESC' | 'ASC';
}
export type BackDatedStatus = 'ALREADY_EXISTS' | 'NOT-FOUND' | 'COMPLETE' | 'IN-PROGRESS' | 'ERROR';
export interface BackDatedHistoryResponse {
  requestedDate: string;
  requestedBy: string;
  tin: string;
  submissionDate: string;
  filterName: string | null;
  filterValue: string | null;
  operator: string;
  status: BackDatedStatus;
  count: number;
  uuids: string[] | null;
  errorMessage: string | null;
}
export interface BulkDownloadHistoryFieldFilters {
  field: string;
  operator: string;
  value: string | boolean | number | string[];
  label: string;
}
export interface BulkDownloadHistory {
  downloadId: number;
  userEmail: string;
  category: string;
  orgId: number;
  tins: string;
  requestedOn: string;
  completedOn: string;
  downloadStatus: string;
  url: string;
  filterCriteria: {
    fieldCriteriaList: BulkDownloadHistoryFieldFilters[];
    sortCriteria: SortCriteria;
  };
  download: string;
  period: string;
}
export interface BackDatedHistoryFetchPayload {
  tins: string[];
  submissionDateFrom: string;
  filterName?: string;
  filterValue?: string;
}
export interface BackDatedHistoryDownloadPayload {
  columns: string[];
  fieldCriteriaList: FieldCriteriaList[];
  sortCriteria: SortCriteria;
}
export interface BulkDownloadCsvPayload {
  tins: string[];
  type?: string;
  category?: string;
  period: string[];
  merge?: boolean;
  filterCriteria: {
    columns: string[];
    fieldCriteriaList: BulkDownloadHistoryFieldFilters[];
    sortCriteria?: SortCriteria;
  };
  source: 'REPO' | 'ARCHIVAL' | 'ARCHIVAL_REPO';
}

export interface Counterparty {
  vendorId: number;
  uniqueIdentifier: string;
  ctpyTin: string;
  ctpyName: string;
  ctpyIdType: string;
  ctpyIdNo: string;
  ctpyAddrLine0: string;
  ctpyAddrLine1: string;
  ctpyAddrLine2: string;
  ctpyPostalZone: string;
  ctpyCityName: string;
  ctpyState: string;
  ctpyCountry: string;
  ctpyEmail: string;
  ctpyContactNo: string;
  ctpySstrNo: string;
  ctpyMsic: string;
  ctpyActivityDesc: string;
  srName: string | null;
  srAddrLine0: string | null;
  srAddrLine1: string | null;
  srAddrLine2: string | null;
  srPostalZone: string | null;
  srCityName: string | null;
  srState: string | null;
  srCountry: string | null;
  srTin: string | null;
  srROrIdType: string | null;
  srRonOrIdNoOrPassportNo: string | null;
}

export interface ShippingList {
  srName: string | null;
  srAddrLine0: string | null;
  srAddrLine1: string | null;
  srAddrLine2: string | null;
  srPostalZone: string | null;
  srCityName: string | null;
  srState: string | null;
  srCountry: string | null;
  srTin: string | null;
  srROrIdType: string | null;
  srRonOrIdNoOrPassportNo: string | null;
}

export interface EmailLogsPayload {
  type: 'Issued' | 'Received';
  emailSource: string;
  status: string | null;
  sentAtFrom: string | null;
  sentAtTo: string | null;
  counterParty: string | null;
  tin: string | null;
}
export interface ReportsListPayload {
  tin: string;
  fromDate: string;
  toDate: string;
  type: string;
  currencyCode?: string;
}

export interface Resp {
  uuid: string;
  longId: string | null;
  dateTimeIssued: string;
  dateTimeValidated: string | null;
}

export interface PeppolData {
  id: string;
  supplierName: string;
  buyerName: string;
  status: string;
  source: string;
  docNumber: string;
  internalDocumentType: string;
  createdDate: string;
  updatedDate: string;
  invoiceTotalTaxAmount: number;
  resp: Resp;
  [key: string]: any;
}

export interface PeppolList {
  data: PeppolData[];
  totalRecords: number;
}

export interface PeppolListPayload {
  fieldCriteriaList: {
    field: FilterKey;
    operator: Operator;
    value: string | string[] | number | number[] | Date | Date[] | boolean;
  }[];
  sortCriteria?: {
    field: string;
    direction: 'ASC' | 'DESC';
  };
  pageNumber?: number;
  pageSize?: number;
}

export interface ArchivedDataResponse {
  requestedOn: string;
  userEmail: string;
  tins: string;
  period: string;
  downloadStatus: string;
  url: string;
}

export interface SearchDocPayload {
  supplyType: string;
  issuerTin: string;
  originalDocsNumber: string[];
}

export interface SearchDocItem {
  docNumber: string;
  totalPayable: string;
  docType: string;
  issuerTin: string;
  uuIds: string;
}

@Injectable({
  providedIn: 'root',
})
export class EinvoiceService extends BaseService {
  private draftId = new BehaviorSubject<any>(null);
  draftIdData = this.draftId.asObservable();

  setDraftId(data: any) {
    this.draftId.next(data);
  }

  updateEinvoice(id: string, payload: any, section: string, isEdit: boolean) {
    return this.httpClient
      .patch(
        `/myportal/update/einvoice/${id}?section=${encodeURIComponent(section)}&isEdit=${isEdit}`,
        payload,
      )
      .pipe(map((resp: any) => this.booleanResp(resp)));
  }

  getTinDetail(tin: string) {
    const params = { tin };

    return this.httpClient.get('/myportal/get/tinDetails', { params }).pipe(
      map((resp: any) => {
        return resp.status ? resp.response : [];
      }),
    );
  }

  einvoiceCounts(
    tin: string | boolean,
    uploaded: boolean,
    type: string | boolean,
  ): Observable<InvoiceCount[]> {
    const params = { tin: tin, uploaded: uploaded, type };
    return this.httpClient.get<ApiResp>('/myportal/einvoice/type/count', { params }).pipe(
      map((resp) => {
        return resp.status ? resp.response : [];
      }),
    );
  }

  bulkUploadResultCount(uid: string) {
    const params = { uid };
    return this.httpClient.get<ApiResp>('/myportal/bulkupload/upload/count', { params }).pipe(
      map((resp) => {
        return resp.status ? resp.response : [];
      }),
    );
  }

  bulkUploadCount(uid: string) {
    const params = { uid, submitted: true };
    return this.httpClient.get<ApiResp>('/myportal/bulkupload/submission/count', { params }).pipe(
      map((resp) => {
        return resp.status ? resp.response : [];
      }),
    );
  }

  createEinvoice(
    tin: string,
    pob: string,
    payload: CreateEInvoice,
  ): Observable<{ status: boolean; message: string; response?: any }> {
    const params = { tin, pob };

    return this.httpClient.post('/myportal/generate/einvoice', payload, { params }).pipe(
      map((resp: any) => {
        const status = this.booleanResp(resp);
        return { status, message: resp.message, response: resp.response };
      }),
    );
  }

  getEinvoiceDetails(
    draftId: string,
    isB2C: boolean = false,
    isArchive?: boolean,
  ): Observable<EInvoiceInfo> {
    const params: any = {};

    if (isB2C) {
      params.isB2cInvoice = 'true';
    }
    if (isArchive) {
      params.archive = 'true';
    }

    return this.httpClient.get<ApiResp>('/myportal/einvoice/detail/' + draftId, { params }).pipe(
      map((resp) => {
        if (!resp.status) this.toastr.error(resp.message, 'Error');
        return resp.status ? resp.response : null;
      }),
    );
  }

  getEinvoiceList(
    payload: EInvoiceListPayload,
    type: string,
    isArchive?: boolean,
  ): Observable<EInvoiceList> {
    const isDocNumber = payload.fieldCriteriaList.find((item) => item.field === 'docNumber');

    if (isDocNumber) {
      const value = isDocNumber.value.toString();
      const separator = value.match(/[,;]/);
      const hasSeparator = /[,;]/.test(value);

      isDocNumber.value = hasSeparator ? value.split(separator![0]).map((i) => i.trim()) : value;
    }

    const mailSentFilter = payload.fieldCriteriaList.find((item) => item.field === 'mailSent');
    if (mailSentFilter) {
      mailSentFilter.value = mailSentFilter.value === 'true';
    }

    /** ✅ Build query params safely */
    const params: any = { type };

    if (isArchive) {
      params.archive = true;
    }

    return this.httpClient.post<ApiResp>('/myportal/einvoices', payload, { params }).pipe(
      map((resp) => {
        if (!resp.status) return null;

        resp.response.data = resp.response.data.map((item: EInvoice) => ({
          ...item,
          lhdn_uuid: item.resp?.uuid,
          lhdn_submissionUID: item.resp?.submissionUid,
          lhdn_longId: item.resp?.longId,
          lhdn_internalId: item.resp?.internalId,
          lhdn_typeName: item.resp?.typeName,
          lhdn_typeVersionName: item.resp?.typeVersionName,
          lhdn_issuerTin: item.resp?.issuerTin,
          lhdn_issuerName: item.resp?.issuerName,
          lhdn_receiverId: item.resp?.receiverId,
          lhdn_receiverName: item.resp?.receiverName,
          lhdn_dateTimeIssued: item.resp?.dateTimeIssued,
          lhdn_dateTimeReceived: item.resp?.dateTimeReceived,
          lhdn_dateTimeValidated: item.resp?.dateTimeValidated,
          lhdn_totalExcludingTax: item.resp?.totalExcludingTax,
          lhdn_totalDiscount: item.resp?.totalDiscount,
          lhdn_totalNetAmount: item.resp?.totalNetAmount,
          lhdn_totalPayableAmount: item.resp?.totalPayableAmount,
          lhdn_rejectRequestDateTime: item.resp?.rejectRequestDateTime,
          lhdn_documentStatusReason: item.resp?.documentStatusReason,
          lhdn_status: item.resp?.status,
          lhdn_sync:
            'isDetailApiCalled' in item
              ? item.isDetailApiCalled === false
                ? 'Pending'
                : 'Complete'
              : '',
        }));

        this.toastr.success(resp.message, 'Success');
        return resp.response;
      }),
    );
  }

  sendMailEinvoice(
    tin: string,
    type: string,
    payload: EInvoiceListPayload,
    prtId: number,
    invType: string,
    isArchive?: boolean,
  ): Observable<EInvoiceList> {
    const params = { tin, type, prtId, invType, ...(isArchive ? { archive: true } : {}) };

    return this.httpClient
      .post<ApiResp>('/myportal/einvoices/attachment', payload, { params })
      .pipe(
        map((resp) => {
          return resp.status ? resp.response : null;
        }),
      );
  }

  confirmMail(payload: EinvoiceEmail, invType: string, tin?: string, isArchive?: boolean) {
    const params: any = { tin, invType, ...(isArchive ? { archive: true } : {}) };

    return this.httpClient.post('/myportal/einvoices/sendMail', payload, { params }).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp.response : [];
      }),
    );
  }

  getBulkUploadEinvoices(data: { tin: string; pobName: string; file: File }): Observable<ApiResp> {
    const params = { tin: data.tin, pob: data.pobName };
    const payload = new FormData();
    payload.append('file', data.file);

    console.log('params', params, payload);
    return this.httpClient
      .post('/myportal/bulk/upload', payload, { params })
      .pipe(map((resp: any) => this.handleFormResp(resp)));
  }

  getUploadHistory(params: { page: number; size: number; tin?: string; pob?: string }) {
    if (params.tin === '') delete params.tin;
    if (params.pob === '') delete params.pob;
    return this.httpClient.get('/myportal/bulkupload/history', { params }).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp.response : [];
      }),
    );
  }

  getEmailHistory(payload: EmailLogsPayload, page: number, size: number, tin?: string) {
    const params: any = { page, size };
    if (tin) params.tin = tin;

    return this.httpClient.post('/myportal/einvoices/mail/history', payload, { params }).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp.response : [];
      }),
    );
  }

  submitEinvoice(payload: SubmitEInvoice) {
    return this.httpClient.post('/myportal/submit/einvoice', payload).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp.response : [];
      }),
    );
  }

  cancelRejectEinvoice(action: 'reject' | 'cancel', payload: cancelEinvoice) {
    return this.httpClient.put(`/myportal/${action}/einvoice`, payload).pipe(
      map((resp: any) => {
        if (!resp.status) this.toastr.error(resp.message, 'Error');
        return resp.status ? resp.response : null;
      }),
    );
  }

  cancelEinvoice(payload: cancelEinvoice) {
    return this.httpClient.put(`/myportal/cancel/einvoice`, payload).pipe(
      map((resp: any) => {
        if (resp.status) {
          const { message, response } = resp;
          const statusMessages =
            response?.response
              ?.map(({ uuid, status }: { uuid: string; status: string }) => `${uuid} : ${status}`)
              .join('\n') || '';

          const successMessage = statusMessages ? `${message}\n${statusMessages}` : message;

          this.toastr.success(successMessage, 'Success');
        } else {
          this.toastr.error(resp.message, 'Error');
        }

        return resp.status ? resp.response : [];
      }),
    );
  }

  rejectEinvoice(payload: cancelEinvoice) {
    return this.httpClient.put(`/myportal/reject/einvoice`, payload).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        else this.toastr.error(resp.message, 'Error');

        return resp.status ? resp.response : [];
      }),
    );
  }

  deleteEinvoice(ids: string) {
    const params = { ids };
    return this.httpClient.delete(`/myportal/einvoices`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        else this.toastr.error(resp.message, 'Error');

        return resp.status;
      }),
    );
  }

  downloadBulkEinvoiceFile(uid: string, isB2cInvoice: boolean) {
    const params: any = { uid, isB2cInvoice };

    return this.httpClient.get<ApiResp>('/myportal/download/bulk/csv', { params }).pipe(
      map((resp: ApiResp) => {
        if (resp.status) {
          const message = resp.response.status ? 'File downloaded!' : 'Download is in progress!';
          const title = resp.response.status ? 'Success' : 'Info';

          this.toastr[resp.response.status ? 'success' : 'info'](message, title);
        } else {
          this.toastr.error(resp.message);
        }
        return resp.status && resp.response.status ? resp.response : null;
      }),
    );
  }

  downloadBulkEinvoiceFileJson(uid: string, isB2cInvoice: boolean) {
    const params: any = { uid, isB2cInvoice };

    return this.httpClient.get<ApiResp>('/myportal/download/bulk/json', { params }).pipe(
      map((resp: ApiResp) => {
        if (resp.status) {
          const message = resp.response.status ? 'File downloaded!' : 'Download is in progress!';
          const title = resp.response.status ? 'Success' : 'Info';

          this.toastr[resp.response.status ? 'success' : 'info'](message, title);
        } else {
          this.toastr.error(resp.message);
        }

        return resp.status && resp.response.status ? resp.response : null;
      }),
    );
  }

  downloadViewEinvoice(data: {
    payload: EInvoiceListPayload;
    type: string;
    isB2C?: boolean;
    isArchive?: boolean;
  }): Observable<ApiResp> {
    const params: any = {
      type: data.isB2C ? 'b2c' : data.type,
      ...(data.isArchive ? { archive: true } : {}),
    };

    return this.httpClient
      .post<ApiResp>('/myportal/download/einvoice', data.payload, { params })
      .pipe(
        map((resp) => {
          if (resp.status) this.toastr.success(resp.message, 'Success');
          return resp;
        }),
      );
  }

  downloadCsv({
    tin,
    type = 'sales',
    isArchive,
  }: {
    tin: string;
    type?: string;
    isArchive?: boolean;
  }) {
    const params: any = {
      tin,
      type,
      ...(isArchive ? { archive: true } : {}),
    };

    return this.httpClient.get<ApiResp>('/myportal/download/einvoice/status', { params }).pipe(
      map((resp) => {
        if (resp && resp.response) {
          const message = resp.response.downloadUrl
            ? 'File downloaded!'
            : 'Download is in progress!';
          const title = resp.response.downloadUrl ? 'Success' : 'Info';
          this.toastr[resp.response.downloadUrl ? 'success' : 'info'](message, title);
        } else {
          this.toastr.error(resp.message);
        }
        return resp.status ? resp : null;
      }),
    );
  }

  printEInvoicePdf(
    payload: EInvoiceListPayload,
    tin: string,
    merge: boolean,
    prtId: number,
    type: string,
    isArchive?: boolean,
  ): Observable<EInvoiceList> {
    const params: any = {
      tin,
      merge,
      prtId,
      type,
      ...(isArchive ? { archive: true } : {}),
    };

    return this.httpClient.post<ApiResp>('/myportal/einvoices/print/pdf', payload, { params }).pipe(
      map((resp) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp.response : null;
      }),
    );
  }

  printHistory(page: number, size: number, tin?: string) {
    const params: any = { page, size };
    if (tin) params.tin = tin;

    return this.httpClient.get('/myportal/einvoices/print/history', { params }).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp.response : [];
      }),
    );
  }

  peppolSharedHistory(page: number, size: number, tin?: string): Observable<ApiResp> {
    const params: any = { page, size };
    if (tin) params.tin = tin;
    return this.httpClient.get('/myportal/pepol/share/logs', { params }).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp : [];
      }),
    );
  }

  peppolHistory(page: number, size: number): Observable<ApiResp> {
    const params: any = { page, size };

    return this.httpClient.get('/myportal/pepol/recieved/history', { params }).pipe(
      map((resp: any) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp : [];
      }),
    );
  }

  verifyEinvoice(uuid: string, tin: string): Observable<ApiResp> {
    return this.httpClient
      .get<ApiResp>(`/myportal/verify/einvoice`, {
        params: { uuid: uuid, tin: tin },
      })
      .pipe(map((resp) => this.handleResp(resp)));
  }

  getPrintData() {
    return this.httpClient
      .get<ApiResp>(`/myportal/einvoices/print/templates`)
      .pipe(map((res) => res.response));
  }

  getSchedulerHistory(payload: SchedulerLogsParams): Observable<SchedulerLogs> {
    const params = { ...payload };
    return this.httpClient.get<ApiResp>(`/myportal/schedular-stats`, { params }).pipe(
      map((resp) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp.response : null;
      }),
    );
  }

  downloadSchedulerStats(submissionIds: string[]) {
    const params = { submissionIds };
    return this.httpClient
      .post('/myportal/schedular-stats/download', params, { responseType: 'blob' })
      .pipe(map((resp) => downloadBlob(resp, 'application/csv', 'SchedulerStats.csv')));
  }

  downloadReceivedLogsStats(ids: string[], usertimezone: string) {
    const params = { ids, usertimezone };
    return this.httpClient
      .post('/myportal/logs/incoming/download', params, { responseType: 'blob' })
      .pipe(map((resp) => downloadBlob(resp, 'application/csv', 'ReceivedLogs.csv')));
  }

  getCustomDownloadFields() {
    return this.httpClient
      .get<ApiResp>(`/myportal/download/fields`)
      .pipe(map((res) => res.response));
  }

  updateCustomDownloadFields(payload: CustomDownload[]) {
    return this.httpClient
      .post<ApiResp>('/myportal/download/fields', payload)
      .pipe(map((resp) => resp.status));
  }

  //refresh incoming invoice
  getRefreshInvoice(payload: { uuid: string | undefined; tin: string }) {
    return this.httpClient.post<ApiResp>('/myportal/refresh/incoming/invoice', payload).pipe(
      map((resp) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        else this.toastr.error(resp.message, 'Error', resp.response);
      }),
    );
  }

  getCopy(payload: { ids: string[] }, type: string) {
    const params = { type };
    return this.httpClient.post<ApiResp>('/myportal/copy/invoice', payload, { params }).pipe(
      map((resp) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        else this.toastr.error(resp.message, 'Error');
        return resp;
      }),
    );
  }

  getVendorAndLabels(params: { category: string; tin: string }): Observable<VendorAndLabels[]> {
    if (!params.tin) {
      params = {
        category: params.category,
        tin: '',
      };
    }
    return this.httpClient.get<ApiResp>(`/myportal/vendor/labels`, { params }).pipe(
      map((resp) => {
        return resp.status ? resp.response : [];
      }),
    );
  }

  getVendorDetails(vendorData: { vendorId: number; uniqueId: string }): Observable<Counterparty> {
    const params = {
      vendorId: vendorData.vendorId,
      uniqueId: vendorData.uniqueId,
    };

    return this.httpClient.get<ApiResp>(`/myportal/vendor`, { params }).pipe(
      map((resp) => {
        return resp.status ? resp.response : [];
      }),
    );
  }

  getReceivedLogs(params: SchedulerLogsParams): Observable<ApiResp | null> {
    return this.httpClient.get<ApiResp>(`/myportal/logs/incoming`, { params: { ...params } }).pipe(
      map((resp) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        return resp.status ? resp : null;
      }),
    );
  }

  // Bulk Download API
  private bulkDownloadSubject = new BehaviorSubject<BulkDownloadHistory[]>([]);
  bulkDownload$ = this.bulkDownloadSubject.asObservable();

  setBulkDownload(data: BulkDownloadHistory[]): void {
    this.bulkDownloadSubject.next(data);
  }

  private isInProgressSubject = new BehaviorSubject<boolean>(false);
  isInProgress$ = this.isInProgressSubject.asObservable();

  setIsInProgress(data: boolean): void {
    this.isInProgressSubject.next(data);
  }

  initiateBulkDownloadRequest(payload: BulkDownloadCsvPayload): Observable<ApiResp> {
    return this.httpClient.post<ApiResp>('/myportal/bulk/download/einvoice', payload).pipe(
      map((res) => {
        if (!res.status) this.toastr.error(res.message, 'Error');
        return res;
      }),
    );
  }

  downloadBulkCsv(
    showInfoAlert = true,
    source: BulkDownloadCsvPayload['source'],
    isArchive?: boolean,
  ) {
    const params: any = {
      source,
      ...(isArchive ? { archive: true } : {}),
    };

    return this.httpClient.get<ApiResp>('/myportal/bulk/download/einvoice/status', { params }).pipe(
      map((resp) => {
        if (resp && resp.response) {
          const message = resp.response.downloadUrl
            ? 'File downloaded!'
            : 'Download is in progress!';
          const title = resp.response.downloadUrl ? 'Success' : 'Info';

          if (title !== 'Info' || (title === 'Info' && showInfoAlert)) {
            this.toastr[resp.response.downloadUrl ? 'success' : 'info'](message, title);
          }
        } else {
          this.toastr.error(resp.message);
        }
        return resp.status ? resp : null;
      }),
    );
  }

  shareViaPeppol(
    payload: SharePepolPayload,
    params: { page: number; size: number; tin: string; invType: string },
  ) {
    return this.httpClient
      .post<ApiResp>(`/myportal/pepol/share/einvoice`, payload, { params })
      .pipe(map((resp) => this.handleFormResp(resp)));
  }

  getReceivedPeppolData(
    params: { type: string },
    payload: PeppolListPayload,
  ): Observable<PeppolList> {
    return this.httpClient.post<ApiResp>(`/myportal/pepol/einvoices`, payload, { params }).pipe(
      map((resp) => {
        if (resp.status) {
          resp.response.data = resp.response.data.map((item: PeppolData) => ({
            ...item,
            resp_uuid: item.resp?.uuid,
            resp_dateTimeIssued: item.resp?.dateTimeIssued,
            resp_dateTimeValidated: item.resp?.dateTimeValidated,
            resp_longId: item.resp?.longId,
          }));
          this.toastr.success(resp.message, 'Success');
          return resp.response;
        }
        return resp.status ? resp : null;
      }),
    );
  }

  getPeppolId(params: { tin: string; supplyType: string }) {
    return this.httpClient.get<ApiResp>(`/myportal/pepol/id`, { params }).pipe(map((resp) => resp));
  }

  getBulkDownloadHistory(
    params: { page: number; size: number; source: string },
    showSuccess = true,
  ) {
    return this.httpClient.get<ApiResp>('/myportal/bulk/download/history', { params }).pipe(
      map((resp) => {
        if (resp.status) {
          if (showSuccess) this.toastr.success(resp.message);
        } else this.toastr.error(resp.fieldErrors?.[0].message ?? resp.message);

        return resp;
      }),
    );
  }

  getBackDatedDataHistory(params: { page: number; size: number }, isRefresh = false) {
    return this.httpClient
      .patch<ApiResp>('/myportal/invoices/backdated-received/history', {}, { params })
      .pipe(
        map((resp) => {
          if (!resp.status) this.toastr.error(resp.fieldErrors?.[0].message ?? resp.message);
          if (resp.status && isRefresh) this.toastr.success(resp.message);
          return resp;
        }),
      );
  }

  fetchBackDatedData(
    payload: BackDatedHistoryFetchPayload,
    params?: { filterName?: string; filterValue?: string },
  ) {
    return this.httpClient
      .post<ApiResp>('/myportal/invoices/backdated-received/fetch', payload, { params })
      .pipe(
        map((resp) => {
          if (resp.status) {
            const tin = payload.tins[0];
            if (tin in resp.response && resp.response[tin].includes('progress')) {
              this.toastr.info(resp.response[tin]);
            } else this.toastr.success(resp.message);
          } else this.toastr.error(resp.fieldErrors?.[0].message ?? resp.message);
          return resp;
        }),
      );
  }

  downloadBackDatedData(payload: BackDatedHistoryDownloadPayload) {
    return this.httpClient
      .post('/myportal/invoices/backdated-received/download', payload, { responseType: 'blob' })
      .pipe(
        map((resp) => {
          const tin = payload.fieldCriteriaList.find((item) => item.field === 'tin')!;
          const date = dayjs().format('DDMMYYYY');

          const fileName = `${tin.value}_Download_${date}.csv`;
          return downloadBlob(resp, 'application/csv', fileName);
        }),
      );
  }
  getReportsList(payload: ReportsListPayload) {
    const params = { ...payload, page: 0, size: 10 };
    return this.httpClient
      .get<ApiResp>('/myportal/currency/code/wise/report', { params })
      .pipe(map((resp) => this.handleResp(resp)));
  }

  downloadReportList(payload: ReportsListPayload) {
    const params = { ...payload, page: 0, size: 10 };
    return this.httpClient
      .get('/myportal/download/currency/code/wise/report', { params, responseType: 'blob' })
      .pipe(map((resp) => downloadBlob(resp, 'application/csv', 'reports.csv')));
  }

  getB2CInvoiceList(
    payload: EInvoiceListPayload,
    type: string = 'b2c',
  ): Observable<EInvoiceList | null> {
    const isDocNumber = payload.fieldCriteriaList.find((item) => item.field === 'docNumber');
    if (isDocNumber) {
      const value = isDocNumber.value.toString();
      const separator = value.match(/[,;]/);
      const hasSeparator = /[,;]/.test(value);
      if (hasSeparator) {
        isDocNumber.value = separator
          ? value.split(separator[0]).map((i) => i.trim())
          : [value.trim()];
      } else {
        isDocNumber.value = value;
      }
    }

    const mailSentFilter = payload.fieldCriteriaList.find((item) => item.field === 'mailSent');
    if (mailSentFilter) mailSentFilter.value = mailSentFilter.value === 'true';

    const params = { type };

    return this.httpClient.post<ApiResp>('/myportal/einvoices/b2c', payload, { params }).pipe(
      map((resp) => {
        if (resp.status) this.toastr.success(resp.message, 'Success');
        else this.toastr.error(resp.message, 'Error');
        return resp.status ? (resp.response as EInvoiceList) : null;
      }),
    );
  }

  searchEinvoiceUuids(payload: SearchDocPayload) {
    return this.httpClient.post<ApiResp>('/myportal/search/doc', payload).pipe(
      map((resp) => {
        if (resp.status) {
          this.toastr.success(resp.message, 'Success');
          return (resp.response ?? []) as SearchDocItem[];
        } else {
          this.toastr.error(resp.message, 'Error');
          return [];
        }
      }),
    );
  }

  // In einvoice.service.ts

  downloadUploadedFileByUid(uid: string): Observable<string | null> {
    const params = { uid };
    // Include headers if strictly required by the CURL, otherwise interceptor usually handles auth
    const headers = { 'x-orgid': '1' };

    return this.httpClient.get<ApiResp>('/myportal/upload', { params, headers }).pipe(
      map((resp) => {
        if (resp.status) {
          // resp.response contains the S3 URL string
          return resp.response;
        } else {
          this.toastr.error(resp.message || 'Download failed');
          return null;
        }
      }),
    );
  }
}
